<script lang="ts">
  import {
    contactById,
    mutedCategories,
    removeContactLocally,
    resumeSending,
    setMute,
  } from '$lib/db/contacts';
  import type { ContactRow } from '$lib/db/db';
  import { formatPhone } from '$lib/format';
  import { t } from '$lib/i18n/index.svelte';
  import { downloadText } from '$lib/media/download';
  import { deleteContactOnServer, exportHistory } from '$lib/protocol/distributor-api';
  import { sessionToken } from '$lib/protocol/session';
  import type { NotificationCategory } from '$lib/protocol/types';
  import { renewCapabilities } from '$lib/push/push';
  import { live } from '$lib/state/live';

  import { goBack, navigate } from '../../app/router.svelte';
  import Banner from '../parts/Banner.svelte';

  let { accountId }: { accountId: string } = $props();

  const contact = live<ContactRow | undefined>(() => contactById(accountId), undefined);
  const muted = live<Set<string>>(() => mutedCategories(accountId), new Set<string>());

  const categories: NotificationCategory[] = ['order_status', 'reminder', 'outage', 'other'];

  let exporting = $state(false);

  // The server's wake-mute list must follow the user's change immediately, not
  // at the next app open: a wake for a just-muted category minutes later reads
  // as the mute not working. A no-op without a push subscription.
  async function toggleMute(category: NotificationCategory, box: HTMLInputElement): Promise<void> {
    await setMute(accountId, category, !box.checked);
    await renewCapabilities([accountId]);
  }

  const keyChanged = $derived(
    $contact !== undefined &&
      $contact.pinnedServerKey.length > 0 &&
      $contact.pinnedServerKey !== $contact.serverKey,
  );

  async function saveHistory(): Promise<void> {
    const current = $contact;
    if (current === undefined || current.token === null) {
      return;
    }
    exporting = true;
    try {
      const token = await sessionToken(current);
      const bundle = await exportHistory({
        serverUrl: current.serverUrl,
        accountId,
        token,
      });
      const date = new Date().toISOString().slice(0, 10);
      downloadText(
        JSON.stringify(bundle, null, 2),
        'application/json',
        `history-${current.phone.replace('+', '')}-${date}.json`,
      );
    } catch {
      // The server may be gone; local history stays either way.
    } finally {
      exporting = false;
    }
  }

  async function trustNewKey(): Promise<void> {
    const current = $contact;
    if (current !== undefined) {
      await resumeSending(accountId, current.serverKey);
    }
  }

  async function deleteContact(): Promise<void> {
    const current = $contact;
    if (current === undefined || !confirm(t('contact.delete.confirm'))) {
      return;
    }
    // Best-effort on the server side: the local wipe must not hinge on a server
    // that may already be unreachable.
    if (current.token !== null) {
      try {
        const token = await sessionToken(current);
        await deleteContactOnServer({ serverUrl: current.serverUrl, accountId, token });
      } catch {
        // Local deletion proceeds regardless.
      }
    }
    await removeContactLocally(accountId);
    navigate({ name: 'threads' });
  }
</script>

<header class="bar">
  <button class="back" onclick={goBack} aria-label={t('common.back')}>‹</button>
  <h1>{t('contact.title')}</h1>
</header>

{#if $contact !== undefined}
  <div class="body">
    <section>
      <h2>{$contact.name.length > 0 ? $contact.name : formatPhone($contact.phone)}</h2>
      <p class="row"><span>{t('contact.number')}</span><span>{formatPhone($contact.phone)}</span></p>
      <p class="row mono"><span>{t('contact.server')}</span><span>{$contact.serverUrl}</span></p>
    </section>

    {#if $contact.status === 'withdrawn'}
      <Banner kind="warn" text={t('contact.withdrawn')} />
    {:else if $contact.status === 'moved'}
      <Banner
        kind="info"
        text={$contact.successorSigned ? t('contact.moved.signed') : t('contact.moved.waiting')}
      />
    {/if}

    {#if keyChanged}
      <section class="keychange">
        <h3>{t('contact.keyChange.title')}</h3>
        <p>{t('contact.keyChange.body')}</p>
        <button class="warnbtn" onclick={() => void trustNewKey()}>{t('contact.keyChange.trust')}</button>
      </section>
    {/if}

    <section>
      <h3>{t('contact.notifications')}</h3>
      {#each categories as category (category)}
        <label class="toggle">
          <span>{t(`contact.cat.${category}`)}</span>
          <input
            type="checkbox"
            checked={!$muted.has(category)}
            onchange={(event) => void toggleMute(category, event.currentTarget as HTMLInputElement)}
          />
        </label>
      {/each}
    </section>

    <section class="actions">
      <button onclick={() => navigate({ name: 'orders', accountId })}>{t('contact.orders')}</button>
      <button disabled={exporting} onclick={() => void saveHistory()}>
        {exporting ? '…' : t('contact.export')}
      </button>
      <button class="danger" onclick={() => void deleteContact()}>{t('contact.delete')}</button>
    </section>
  </div>
{/if}

<style>
  .bar {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 10px 12px;
  }
  .back {
    border: 0;
    background: transparent;
    font-size: 28px;
    cursor: pointer;
    color: var(--ink);
    padding: 0 8px;
  }
  h1 {
    margin: 0;
    font-size: 20px;
  }
  .body {
    padding: 0 16px 24px;
    display: flex;
    flex-direction: column;
    gap: 18px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }
  section h2 {
    margin: 0 0 10px;
    font-size: 22px;
  }
  section h3 {
    margin: 0 0 8px;
    font-size: 15px;
    color: var(--ink-soft);
  }
  .row {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin: 6px 0;
    font-size: 15px;
  }
  .mono span:last-child {
    font-family: ui-monospace, monospace;
    font-size: 13px;
    overflow-wrap: anywhere;
    text-align: right;
  }
  .toggle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid var(--line);
  }
  .keychange {
    background: var(--warn-bg);
    color: var(--warn-ink);
    border-radius: var(--radius);
    padding: 14px;
  }
  .keychange p {
    margin: 0 0 10px;
    font-size: 14px;
  }
  .warnbtn {
    border: 1px solid currentcolor;
    background: transparent;
    color: inherit;
    border-radius: 999px;
    padding: 8px 16px;
    font-weight: 600;
    cursor: pointer;
  }
  .actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .actions button {
    border: 1px solid var(--line);
    background: var(--surface);
    color: var(--ink);
    border-radius: 10px;
    padding: 12px;
    font-size: 15px;
    cursor: pointer;
    text-align: left;
  }
  .actions .danger {
    color: var(--danger);
    border-color: var(--danger);
  }
</style>
